import { LuBrainCircuit, LuBot, LuWorkflow } from "react-icons/lu";

import { NAV_SECTIONS } from "./nav-sections";

/**
 * Single source of truth for the agent configuration surface.
 *
 * Both the main agent and every sub-agent render the SAME set of tabs; only the
 * base path differs. `buildAgentNavigation` produces the tab list for whichever
 * scope is active, so the two never drift apart.
 */

// route segment shared by every sub-agent scope: /sub-agents/:subAgentId/...
export const SUB_AGENT_BASE = "/sub-agents";

/**
 * Tabs of the agent config surface, in the order shown in the design.
 *
 * `scopes` marks where a tab is meaningful:
 *   - "agent"     -> only on the top-level agent
 *   - "sub-agent" -> only inside a sub-agent
 *   - both        -> rendered in either scope
 *
 * Sub-agents get the same six tabs as the top-level agent. Their configuration
 * is stored per sub-agent under `toolData`; the runtime today executes a
 * sub-agent from `instructions` and `tools` only, so the remaining sections are
 * saved and ready but do not yet change runtime behaviour.
 */
export const AGENT_CONFIG_TABS = [
  {
    key: "instructions",
    name: "Instructions",
    href: "/agent",
    subHref: "",
    scopes: ["agent", "sub-agent"],
  },
  {
    key: "skills",
    name: "Skills",
    href: "/skills",
    subHref: "/skills",
    scopes: ["agent", "sub-agent"],
  },
  {
    key: "tools",
    name: "Tools",
    href: "/tools",
    subHref: "/tools",
    scopes: ["agent", "sub-agent"],
  },
  {
    key: "mcp",
    name: "MCP",
    href: "/mcp-servers",
    subHref: "/mcp-servers",
    scopes: ["agent", "sub-agent"],
  },
  {
    key: "memory",
    name: "Memory",
    href: "/memory",
    subHref: "/memory",
    scopes: ["agent", "sub-agent"],
  },

  // {
  //   key: "llm",
  //   name: "LLM",
  //   href: "/llm-settings",
  //   subHref: "/llm",
  //   scopes: ["agent", "sub-agent"],
  // },
];

/**
 * Build the tab bar for a given scope.
 *
 * @param {object}  options
 * @param {string}  [options.subAgentId] when set, tabs point into that sub-agent
 * @returns {Array<{name: string, href: string, current: boolean}>}
 *          shape consumed by <PageContainer navigation={...} /> / InternalHeader
 */
export function buildAgentNavigation({ subAgentId } = {}) {
  const scope = subAgentId ? "sub-agent" : "agent";

  return AGENT_CONFIG_TABS.filter((tab) => tab.scopes.includes(scope)).map(
    (tab) => ({
      name: tab.name,
      href: subAgentId
        ? `${SUB_AGENT_BASE}/${subAgentId}${tab.subHref}`
        : tab.href,
      current: false,
      // `end` keeps the Instructions tab from staying active on child routes,
      // since its sub-agent href is the scope root and would prefix-match all.
      end: subAgentId ? tab.subHref === "" : false,
    })
  );
}

/** Tab bar for the top-level agent. */
export const AGENT_NAVIGATION = buildAgentNavigation();

/** Left-rail entry that opens the top-level agent config surface. */
export const AGENT_ROOT_NAV_ITEM = {
  name: "Agent",
  href: "/agent",
  active: false,
  icon: <LuBrainCircuit />,
  tip: "Instructions, skills, tools, MCP and model for this agent",
  section: NAV_SECTIONS.BUILD,
};

/** Left-rail entry opening the sub-agent list. */
export const SUB_AGENTS_NAV_GROUP = {
  name: "Sub-Agents",
  href: SUB_AGENT_BASE,
  active: false,
  icon: <LuBot />,
  tip: "Specialized agents the main agent can delegate to",
  section: NAV_SECTIONS.BUILD,
};

/** Icon used for each sub-agent row inside the group. */
export const SUB_AGENT_ITEM_ICON = <LuBot />;

/** Icon used for each workflow row inside the workflows group. */
export const WORKFLOW_ITEM_ICON = <LuWorkflow />;
